// ...existing code...
/**
 * Update neuron glow/bloom based on activation
 * Ensures neurons visually persist and bloom while activated, similar to axons
 */
/**
 * Update neuron glow/bloom based on activation
 * Ensures neurons visually persist and bloom while activated, similar to axons
 */
// ...existing code...
// ...existing code...
/**
 * Update neuron glow/bloom based on activation
 * Ensures neurons visually persist and bloom while activated, similar to axons
 */
// ...existing code...
// ...existing code...
// ...existing code...

import { getCustomGlowShader } from "./custom-glow-shader"
import type { Neuron, Connection, NetworkParams } from "./DendriticNetwork"

/**
 * Visual effects controller for neural network rendering
 */

type ThreeColor = { new (r: number, g: number, b: number): any }
type ThreeUniformsUtils = { merge: (args: any[]) => any }
type ThreeUniformsLib = { fog: any }
type ThreeShaderMaterial = new (args: any) => any

interface ThreeLike {
  Color: ThreeColor
  UniformsUtils: ThreeUniformsUtils
  UniformsLib: ThreeUniformsLib
  ShaderMaterial: ThreeShaderMaterial
}

interface DendriticNetwork {
  neurons: Neuron[]
}

interface BloomPass {
  strength: number
  threshold: number
  radius: number
}

export class VisualEffects {
  private THREE: ThreeLike
  private network: DendriticNetwork
  private bloomPass: BloomPass

  constructor(
    THREE: ThreeLike,
    network: DendriticNetwork,
    bloomPass: BloomPass,
  ) {
    this.THREE = THREE
    this.network = network
    this.bloomPass = bloomPass
  }

  /**
   * Update atmospheric effects based on network activity
   */
  updateAtmosphericEffects(
    elapsedTime: number,
    params: Partial<NetworkParams>,
  ): void {
    if (!this.bloomPass) return

    const activeNeurons = this.network.neurons.filter(
      (n: Neuron) => n.activation > 0.001,
    ).length
    const mediumActivityNeurons = this.network.neurons.filter(
      (n: Neuron) => n.activation > 0.1,
    ).length
    const highActivityNeurons = this.network.neurons.filter(
      (n: Neuron) => n.activation > 0.5,
    ).length
    const ultraHighActivityNeurons = this.network.neurons.filter(
      (n: Neuron) => n.activation > 0.8,
    ).length

    const totalNeurons = this.network.neurons.length
    const lowActivityLevel = activeNeurons / totalNeurons
    const mediumActivityLevel = mediumActivityNeurons / totalNeurons
    const highActivityLevel = highActivityNeurons / totalNeurons
    const ultraActivityLevel = ultraHighActivityNeurons / totalNeurons

    // Sophisticated bloom strength that responds to wave propagation
    const baseStrength = params.bloomStrength || 0.8 // Further reduced base strength
    const lowActivityBoost = lowActivityLevel * 0.2 // Further reduced boost
    const mediumActivityBoost = mediumActivityLevel * 0.5 // Further reduced boost
    const highActivityBoost = highActivityLevel * 1.0 // Further reduced boost
    const ultraActivityBoost = ultraActivityLevel * 2.0 // Further reduced boost

    this.bloomPass.strength =
      baseStrength +
      lowActivityBoost +
      mediumActivityBoost +
      highActivityBoost +
      ultraActivityBoost

    // Dynamic threshold that makes slow waves more visible
    const waveThreshold = Math.max(0.05, 0.1 - highActivityLevel * 0.05) // Increased base threshold, reduced dynamic range
    this.bloomPass.threshold = waveThreshold

    // Adjust bloom radius based on propagation spread
    const propagationSpread = lowActivityLevel * 0.5 // Further reduced spread impact
    this.bloomPass.radius = Math.max(0.5, 0.8 + propagationSpread) // Further reduced base radius

    // Track dramatic moments for extra bloom bursts
    if (ultraActivityLevel > 0.1) {
      this.bloomPass.strength *= 1.1 // Further reduced boost
      this.bloomPass.threshold *= 0.9 // Further reduced threshold reduction
    }
  }

  /**
   * Update connection glow effects with optimized performance
   */
  updateConnectionGlow(
    dendriteMesh: {
      children: {
        userData?: {
          isAxon?: boolean
          startPos?: { distanceToSquared: (v: any) => number }
          endPos?: { distanceToSquared: (v: any) => number }
        }
        material?: any
        position?: any
      }[]
    },
    elapsedTime: number,
  ): void {
    if (!dendriteMesh) return

    const tubes = dendriteMesh.children
    if (!tubes || tubes.length === 0) return

    // Build a lookup map for faster neuron access
    const neuronMap = new Map<string, Neuron>()
    for (const neuron of this.network.neurons) {
      neuronMap.set(neuron.id, neuron)
    }

    tubes.forEach((tube) => {
      if (!tube.userData || !tube.userData.isAxon) {
        this.updateDendriteGlow(tube, neuronMap)
        return
      }

      // Axon processing - optimized connection matching
      let connectionFound = false

      for (const neuron of this.network.neurons) {
        if (connectionFound) break

        for (const connection of neuron.connections) {
          const targetNeuron = neuronMap.get(connection.targetNeuronId)
          if (!targetNeuron) continue

          // Optimized distance check with early exit, guard against undefined
          const startPos = tube.userData.startPos
          if (!startPos) continue
          const startDist = startPos.distanceToSquared(neuron.position)
          if (startDist > 1.0) continue

          const endPos = tube.userData.endPos
          if (!endPos) continue
          const endDist = endPos.distanceToSquared(targetNeuron.position)
          if (endDist > 1.0) continue

          connectionFound = true
          this.updateAxonGlow(tube, connection, elapsedTime)
          break
        }
      }

      if (!connectionFound) {
        // Default inactive axon state: NO glow, very subtle color, low opacity
        if (tube.material) tube.material.dispose()
        const customShader = getCustomGlowShader(this.THREE)
        const uniforms = this.THREE.UniformsUtils.merge([
          this.THREE.UniformsLib.fog,
          customShader.uniforms,
          {
            baseColor: { value: new this.THREE.Color(0.18, 0.22, 0.32) }, // subtle blue-gray
            emissiveColor: { value: new this.THREE.Color(0, 0, 0) }, // no glow
            emissiveIntensity: { value: 0.0 },
            opacity: { value: 0.18 },
          },
        ])

        tube.material = new this.THREE.ShaderMaterial({
          uniforms: uniforms,
          vertexShader: customShader.vertexShader,
          fragmentShader: customShader.fragmentShader,
          transparent: true,
          fog: true,
        })
      }
    })
  }

  /**
   * Update axon glow based on connection activity
   */
  private updateAxonGlow(
    tube: { material?: any },
    connection: Connection & {
      activationIntensity?: number
      isNewConnection?: boolean
      creationTime?: number
    },
    elapsedTime: number,
  ): void {
    let baseColor, emissiveColor, emissiveIntensity, opacity

    if ((connection.activationIntensity ?? 0) > 0.05) {
      // Active connection - more intense visible glow
      const intensity = Math.min(1.0, connection.activationIntensity ?? 0)
      const powerIntensity = Math.pow(intensity, 0.7) // slightly less flattening

      baseColor = new this.THREE.Color(
        Math.min(0.7, 0.32 + powerIntensity * 0.8),
        Math.min(0.85, 0.42 + powerIntensity * 1.0),
        Math.min(1.0, 0.72 + powerIntensity * 1.2),
      )
      opacity = Math.min(1.0, 0.7 + powerIntensity * 0.3)

      emissiveColor = new this.THREE.Color(
        Math.min(0.35, powerIntensity * 0.22),
        Math.min(0.45, powerIntensity * 0.28),
        Math.min(0.7, Math.max(0.18, powerIntensity * 0.38)),
      )
      emissiveIntensity = Math.min(0.9, 0.22 + powerIntensity * 0.45)

      // Highlight new connections with special effects
      if (
        connection.isNewConnection &&
        connection.creationTime !== undefined &&
        elapsedTime - connection.creationTime < 5.0
      ) {
        const birthGlow =
          Math.sin((elapsedTime - connection.creationTime) * 3.0) * 0.5 + 0.5
        emissiveIntensity = Math.min(1.2, emissiveIntensity + birthGlow * 0.28)
      }
    } else {
      // Inactive axon - NO glow, just a very subtle base color
      baseColor = new this.THREE.Color(0.18, 0.22, 0.32)
      opacity = 0.18
      emissiveColor = new this.THREE.Color(0, 0, 0)
      emissiveIntensity = 0.0
    }

    if (tube.material) tube.material.dispose()
    const customShader = getCustomGlowShader(this.THREE)
    const uniforms = this.THREE.UniformsUtils.merge([
      this.THREE.UniformsLib.fog,
      customShader.uniforms,
      {
        baseColor: { value: baseColor },
        emissiveColor: { value: emissiveColor },
        emissiveIntensity: { value: emissiveIntensity },
        opacity: { value: opacity },
      },
    ])

    tube.material = new this.THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: customShader.vertexShader,
      fragmentShader: customShader.fragmentShader,
      transparent: true,
      fog: true,
    })
  }

  /**
   * Update dendrite glow based on nearby activity
   */
  private updateDendriteGlow(
    tube: {
      material?: any
      position?: { distanceToSquared: (v: any) => number }
    },
    neuronMap: Map<string, Neuron>,
  ): void {
    let nearbyActivity = 0
    const tubePosition = tube.position
    if (!tubePosition) {
      // If no position, skip glow update
      return
    }

    // Sample only a subset of neurons for performance
    const sampleRate = Math.max(1, Math.floor(this.network.neurons.length / 50))
    for (let i = 0; i < this.network.neurons.length; i += sampleRate) {
      const neuron = this.network.neurons[i]
      if (neuron.activation <= 0.1) continue

      const distanceSquared = tubePosition.distanceToSquared(neuron.position)
      if (distanceSquared < 225) {
        const distance = Math.sqrt(distanceSquared)
        nearbyActivity = Math.max(
          nearbyActivity,
          neuron.activation * (1.0 - distance / 15),
        )
      }
    }

    if (tube.material) tube.material.dispose()
    const customShader = getCustomGlowShader(this.THREE)
    const uniforms = this.THREE.UniformsUtils.merge([
      this.THREE.UniformsLib.fog,
      customShader.uniforms,
      {
        baseColor: {
          value: new this.THREE.Color(
            Math.min(1.0, 0.1 + nearbyActivity * 0.3),
            Math.min(1.0, 0.15 + nearbyActivity * 0.4),
            Math.min(1.0, 0.25 + nearbyActivity * 0.6),
          ),
        },
        emissiveColor: {
          value: new this.THREE.Color(
            Math.min(1.0, nearbyActivity * 0.1),
            Math.min(1.0, nearbyActivity * 0.15),
            Math.min(1.0, nearbyActivity * 0.2),
          ),
        },
        emissiveIntensity: { value: nearbyActivity * 0.2 },
        opacity: { value: 0.2 + nearbyActivity * 0.3 },
      },
    ])

    tube.material = new this.THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: customShader.vertexShader,
      fragmentShader: customShader.fragmentShader,
      transparent: true,
      fog: true,
    })
  }
}
